// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getAccount } from "@insite/client-framework/Services/AccountService";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain, NavigateToReturnUrl } from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { xngCheckIsDisplayModal } from "@rc/App/Services/TermsAndConditions/termsAndConditions";

export const XngNavigateToReturnUrl: any = async (props: any) => {
    if (props.parameter.skipRedirect) {
        return;
    }

    const state = props.getState();
    const session = getSession(state);
    const homePageUrl = getPageLinkByPageType(state, "HomePage")?.url;
    let languageSpecificLandingPage = session.customLandingPage;
    if (homePageUrl && homePageUrl !== "/") {
        if (languageSpecificLandingPage && languageSpecificLandingPage.startsWith("/")) {
            languageSpecificLandingPage = homePageUrl.concat(session.customLandingPage);
        } else {
            languageSpecificLandingPage = homePageUrl.concat("/", session.customLandingPage);
        }
    }
    const defaultUrl = session.dashboardIsHomepage
        ? getPageLinkByPageType(state, "MyAccountPage")?.url
        : session.customLandingPage;

    const checkoutShippingUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    let returnUrl = props.parameter.returnUrl ? decodeURIComponent(props.parameter.returnUrl) : undefined;
    const { userId } = getQueryStrings(state);
    const userSetupPage = getPageLinkByPageType(state, "UserSetupPage");
    const dashboardPageUrl = getPageLinkByPageType(props.getState(), "MyAccountPage")?.url || "MyAccount";
    const userSetupPageUrl = userSetupPage?.url;
    if (props.apiParameter.session.shipToId === "") {
        returnUrl = `${getPageLinkByPageType(state, "AddressesPage")?.url}?isNewShipTo=true`;
    } else if (returnUrl?.toLowerCase() === checkoutShippingUrl?.toLowerCase()) {
        const cartResult = await getCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: ["cartLines", "hiddenproducts"],
        } as GetCartApiParameter);
        const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;
        const checkoutReviewAndSubmitUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
        const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;
        if (!canCheckOut || session.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    } else if (
        returnUrl &&
        userSetupPageUrl &&
        returnUrl.toLocaleLowerCase().indexOf(userSetupPageUrl.toLocaleLowerCase()) > -1 &&
        userId
    ) {
        // If customer is not assigned to current user, return to UserAdministration page.
        try {
            await getAccount({ accountId: userId, expand: ["approvers", "roles"] });
        } catch (error) {
            returnUrl = getPageLinkByPageType(state, "UserListPage")?.url;
        }
    }

    // xng-change - RCS-1057: redirect to the returnUrl to allow tc=show
    // xng-change - RC-2302: Fix redirect to dashboard after select customer
    let url = "";
    if (!returnUrl?.includes(dashboardPageUrl) && returnUrl?.startsWith("/?")) {
        url = `${defaultUrl}${returnUrl}` || getHomePageUrl(state);
    } else if (returnUrl) {
        url = `${returnUrl}` || getHomePageUrl(state);
    } else {
        url = getHomePageUrl(state);
    }

    if (!url.includes("tc=show")) {
        url += url.includes("?") ? "&tc=show" : "?tc=show";
    }
    url = url.replace("/?", "?");
    window.location.href = url;
};

replaceInChain(chain, NavigateToReturnUrl, XngNavigateToReturnUrl);
